import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { DataQualitySnapshot } from '@/types/webSearch';
import { TrendingUp, TrendingDown, Minus, BarChart3, Clock, Crown } from 'lucide-react';
import { format } from 'date-fns';
import { computeOverallDataQuality, KeyContactsStats } from '@/utils/dataQualityUtils';
import { KeyContact } from '@/types/lead';

interface DataQualityModalProps {
  dataQuality?: DataQualitySnapshot | null;
  dataQualityHistory?: DataQualitySnapshot[];
  keyContacts?: KeyContact[];
}

const STAGE_LABELS: Record<string, string> = {
  web_search: 'Web Search',
  deep_research: 'Deep Research',
  crawl: 'Website Crawl',
};

const ALL_STAGES = ['web_search', 'deep_research', 'crawl'];

const DIMENSION_LABELS: Record<string, string> = {
  contactInfo: 'Contact Info',
  companyInfo: 'Company Info',
  onlinePresence: 'Online Presence',
  servicesProducts: 'Services & Products',
  teamLeadership: 'Team & Leadership',
};

function getScoreColor(score: number): string {
  if (score >= 76) return 'text-green-600 dark:text-green-400';
  if (score >= 51) return 'text-blue-600 dark:text-blue-400';
  if (score >= 26) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
}

function getBadgeVariant(label: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (label === 'Excellent') return 'default';
  if (label === 'Good') return 'secondary';
  return 'outline';
}

function getProgressColor(score: number): string {
  if (score >= 76) return 'bg-green-500';
  if (score >= 51) return 'bg-blue-500';
  if (score >= 26) return 'bg-orange-500';
  return 'bg-red-500';
}

function getDimensionBarColor(value: number): string {
  if (value >= 4) return 'bg-green-500';
  if (value >= 3) return 'bg-blue-500';
  if (value >= 2) return 'bg-orange-500';
  return 'bg-red-500';
}

const DataQualityModal: React.FC<DataQualityModalProps> = ({ dataQuality, dataQualityHistory, keyContacts }) => {
  if (!dataQuality) return null;

  const history = dataQualityHistory || [];

  const keyContactsStats: KeyContactsStats | undefined = keyContacts ? {
    total: keyContacts.length,
    withEmail: keyContacts.filter(c => !!c.email).length,
    withPhone: keyContacts.filter(c => !!c.phone).length,
  } : undefined;

  const overall = computeOverallDataQuality(history, dataQuality, keyContactsStats);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border border-border hover:bg-muted transition-colors">
          <BarChart3 className="h-3.5 w-3.5" />
          <span className={getScoreColor(overall.score)}>{overall.score}</span>
          <Badge variant={getBadgeVariant(overall.label)} className="text-[10px] px-1 py-0 h-4">
            {overall.label}
          </Badge>
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Data Quality Assessment
          </DialogTitle>
        </DialogHeader>

        {/* Overall Assessment */}
        <div className="space-y-3 rounded-lg border-2 border-primary/20 bg-primary/5 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Crown className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Overall Assessment</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Combined Score</span>
            <span className={`text-2xl font-bold ${getScoreColor(overall.score)}`}>
              {overall.score}/100
            </span>
          </div>
          <div className="relative h-3 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className={`h-full rounded-full transition-all ${getProgressColor(overall.score)}`}
              style={{ width: `${overall.score}%` }}
            />
          </div>
          {/* Overall dimension breakdown */}
          <div className="space-y-1.5 mt-2">
            {Object.entries(DIMENSION_LABELS).map(([key, label]) => {
              const value = overall.breakdown[key as keyof typeof overall.breakdown] || 0;
              return (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-[11px] text-muted-foreground w-28 shrink-0">{label}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div
                      className={`h-full rounded-full ${getDimensionBarColor(value)}`}
                      style={{ width: `${(value / 5) * 100}%` }}
                    />
                  </div>
                  <span className="text-[11px] font-medium w-4 text-right">{value}</span>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground italic">{overall.summary}</p>
        </div>

        {/* Stage Timeline */}
        <div className="space-y-3 mt-4">
          <h4 className="text-sm font-semibold">Enrichment Progress</h4>
          {ALL_STAGES.map((stageKey, idx) => {
            const snapshot = history.find(h => h.stage === stageKey);
            const prevSnapshot = idx > 0 ? history.find(h => h.stage === ALL_STAGES[idx - 1]) : null;
            const scoreDiff = snapshot && prevSnapshot ? snapshot.score - prevSnapshot.score : null;

            return (
              <div key={stageKey} className={`rounded-lg border p-3 ${snapshot ? 'border-border' : 'border-dashed border-muted-foreground/30 opacity-50'}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${snapshot ? getProgressColor(snapshot.score) : 'bg-muted-foreground/30'}`} />
                    <span className="text-sm font-medium">{STAGE_LABELS[stageKey]}</span>
                  </div>
                  {snapshot ? (
                    <div className="flex items-center gap-2">
                      <span className={`text-lg font-bold ${getScoreColor(snapshot.score)}`}>{snapshot.score}</span>
                      {scoreDiff !== null && scoreDiff !== 0 && (
                        <span className={`text-xs flex items-center gap-0.5 ${scoreDiff > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {scoreDiff > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                          {scoreDiff > 0 ? '+' : ''}{scoreDiff}
                        </span>
                      )}
                      {scoreDiff === 0 && (
                        <span className="text-xs flex items-center gap-0.5 text-muted-foreground">
                          <Minus className="h-3 w-3" /> 0
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">Not yet run</span>
                  )}
                </div>

                {snapshot && (
                  <>
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-2">
                      <Clock className="h-3 w-3" />
                      {format(new Date(snapshot.timestamp), 'MMM d, yyyy HH:mm')}
                    </div>

                    {/* Dimension breakdown */}
                    <div className="space-y-1.5">
                      {Object.entries(DIMENSION_LABELS).map(([key, label]) => {
                        const value = snapshot.breakdown[key as keyof typeof snapshot.breakdown] || 0;
                        return (
                          <div key={key} className="flex items-center gap-2">
                            <span className="text-[11px] text-muted-foreground w-28 shrink-0">{label}</span>
                            <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                              <div
                                className={`h-full rounded-full ${getDimensionBarColor(value)}`}
                                style={{ width: `${(value / 5) * 100}%` }}
                              />
                            </div>
                            <span className="text-[11px] font-medium w-4 text-right">{value}</span>
                          </div>
                        );
                      })}
                    </div>

                    <p className="text-[11px] text-muted-foreground mt-2 italic">{snapshot.summary}</p>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DataQualityModal;
